import { useState, useRef, useEffect } from 'react';
import { IconButton, Button, ButtonGroup, Badge, Slider, Typography, Paper, Box } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import SubtractIcon from '@mui/icons-material/Remove';
import SpeedIcon from '@mui/icons-material/Speed';
import CopyLinkButton from './CopyLinkButton'
import { useTheme } from '@mui/material/styles'

function AudioPlayer({ download, stream }) {
  const theme = useTheme();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const minPlaybackSpeed = 0.1, maxPlaybackSpeed = 2.5;

  const togglePlayPause = () => {
    // console.log(audioRef.current);
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const updatePlaybackSpeed = (newPlaybackSpeed) => {
    if (newPlaybackSpeed >= minPlaybackSpeed && newPlaybackSpeed <= maxPlaybackSpeed) {
      setPlaybackSpeed(newPlaybackSpeed);
      audioRef.current.playbackRate = newPlaybackSpeed;
    }
  }

  const handleTimeUpdate = () => {
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    setDuration(audioRef.current.duration);
  };

  const resetPlayback = () => {
    setCurrentTime(0);
    setIsPlaying(false);
  };

  const handleSliderChange = (event, newValue) => {
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  useEffect(() => {
    if (stream != null && download != null) {
      resetPlayback();
      updatePlaybackSpeed(playbackSpeed);
    }
  }, [stream, download]);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        justifyContent: 'center',
        backgroundColor: (stream == null && download == null) ? theme.palette.action.disabledBackground : '',
      }}
    >
      <audio
        ref={audioRef}
        src={stream}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={resetPlayback}
      />
      <IconButton disabled={stream==null} onClick={togglePlayPause}>
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>
      <Slider
        disabled={stream==null}
        value={currentTime}
        max={duration}
        onChange={handleSliderChange}
        aria-labelledby="continuous-slider"
        step={.1}
        sx={{
          '& .MuiSlider-thumb': {
            transition: 'left 300ms linear',
          },
          '& .MuiSlider-track': {
            transition: 'width 300ms linear',
          },
          '@media (max-width: 380px)': {
            display: 'none',
          },
        }}
      />
      <ButtonGroup
        size="small"
        variant="contained"
      >
        <Button
          disabled
          sx={{
            "&.MuiButtonBase-root": {
              paddingRight: '2.6rem',
            }
          }}
        >
          <Badge
            badgeContent={playbackSpeed.toFixed(1) + "x"}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: '0.6rem',
                fontWeight: 'bold',
                textTransform: 'lowercase',
                right: '-1rem',
                top: '50%',
                padding: '0 .3rem',
                backgroundColor: 'rgba(255,255,255,.3)',
                color: 'gray',
              }
            }}
          >
            <SpeedIcon sx={{ display: {xs: 'none', sm: 'block'} }} />
          </Badge>
        </Button>
        <Button
          disabled={stream==null}
          onClick={() => updatePlaybackSpeed(playbackSpeed - .1)}
        ><SubtractIcon/></Button>
        <Button
          disabled={stream==null}
          onClick={() => updatePlaybackSpeed(playbackSpeed + .1)}
        ><AddIcon/></Button>
      </ButtonGroup>
      <Typography
        sx={{
          whiteSpace: 'nowrap',
          display: {xs: 'none', sm: 'block'},
        }}
        variant="body2"
      >
        {`${currentTime.toFixed(1)}/${duration.toFixed(1)}s`}
      </Typography>
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
      }}>
        <IconButton disabled={download==null} href={download} download>
          <DownloadIcon />
        </IconButton>
        <CopyLinkButton url={window.location.href} />
      </Box>
    </Paper>
  );
}

export default AudioPlayer;